<template lang="pug">
    div(id="ocean")
</template>

<script>
    import VueTypes from 'vue-types';
    import * as THREE from 'three';
    import { Water } from 'three/examples/jsm/objects/Water';
    import { Sky } from 'three/examples/jsm/objects/Sky';

    import textureImage from './waternormals-min.jpg';
    import raindrop from './raindrop-min.png';

    // Create three.js scene
    let scene = new THREE.Scene({ autoUpdate: true });

    let camera;

    // Create renderer
    let renderer = new THREE.WebGLRenderer({ alpha: 1 });

    // Create light
    let light = new THREE.DirectionalLight(0xffffff, 100);

    // Create water geometry
    let waterGeometry = new THREE.PlaneBufferGeometry(1400, 1400);

    // Create ocean with params
    let water = new Water(waterGeometry, {
        textureWidth: 512,
        textureHeight: 512,
        waterNormals: new THREE.TextureLoader().load(textureImage, function (
            texture
        ) {
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        }),
        sunDirection: light.position.clone().normalize(),
        alpha: 1,
        sunColor: 0x2e476f,
        waterColor: 0x06427f,
        distortionScale: 3,
        fog: true
    });

    // Rain drops size
    let rainCount = 14000;

    // Create rain geometry
    let rainGeo = new THREE.Geometry();

    // Create rain material
    let rainMaterial = new THREE.PointCloudMaterial({
        color: 0xffffff,
        size: 2,
        map: new THREE.TextureLoader().load(raindrop),
        blending: THREE.AdditiveBlending,
        depthTest: false,
        transparent: true
    });

    // Create rain with params
    let rain = new THREE.PointCloud(rainGeo, rainMaterial);

    // Create sky
    let sky = new Sky();

    // Create cube camera for background
    let cubeCamera = new THREE.CubeCamera(0.8, 0.8, 512);
    let animationFrame;

    export default {
        props: {
            mood: VueTypes.number.def(50),
            showMood: VueTypes.bool.def(false)
        },
        watch: {
            /**
             * Update sun position and rain on mood prop change.
             */
            mood() {
                this.updateSun();
                this.updateRain();
            },

            /**
             * Show Mood.
             * @param val
             */
            showMood(val) {
                if (val) {
                    this.render();
                }
            }
        },
        mounted() {
            // Init Ocean
            this.init();

            // Animate Ocean
            this.animate();

            if (this.mood <= 40) {
                this.updateRain();
            }
        },
        methods: {
            /**
             * Init Ocean.
             */
            init() {
                // Set width and height by screen resolution
                let width = document.getElementById('ocean').offsetWidth;
                let height = (width / 16) * 9;

                if (window.innerWidth < 600) {
                    height = 510;
                }

                // Set height for mobile devices
                if (
                    window.innerWidth < 600 &&
                    window.innerHeight < window.innerWidth
                ) {
                    height = window.innerHeight;
                }

                if (window.innerHeight < 480) {
                    height = window.innerHeight;
                }

                // Init Camera
                camera = new THREE.PerspectiveCamera(55, width / height, 1, 18000);

                // Set Camera position
                camera.position.set(30, 30, 100);

                // scene.add(light);

                // Set params for sky
                let uniforms = sky.material.uniforms;
                uniforms['turbidity'].value = 5;
                uniforms['rayleigh'].value = 2.8;
                uniforms['luminance'].value = 0.8;
                uniforms['mieCoefficient'].value = 0.005;
                uniforms['mieDirectionalG'].value = 0.9;

                // Set params for cube camera
                cubeCamera.renderTarget.texture.generateMipmaps = true;
                cubeCamera.renderTarget.texture.minFilter =
                    THREE.LinearMipmapLinearFilter;
                scene.background = cubeCamera.renderTarget;

                // Update Sun
                this.updateSun();

                // Set Ocean position
                water.rotation.x = -Math.PI / 2.1;

                // Set raindrops positions for animate it
                for (let i = 0; i < rainCount; i++) {
                    let rainDrop = new THREE.Vector3(
                        Math.random() * 500 - 250,
                        Math.random() * 500 - 250,
                        Math.random() * 500 - 250
                    );
                    rainDrop.velocity = {};
                    rainDrop.velocity = 0;
                    rainGeo.vertices.push(rainDrop);
                }

                // Add Ocean to scene
                scene.add(water);

                // Change camera position
                camera.position.z = 40;

                // Set render sizes
                renderer.setSize(width, height);

                // Add Ocean to DOM
                document.getElementById('ocean').appendChild(renderer.domElement);

                // Add listener on resize
                window.addEventListener('resize', this.onWindowResize, false);
            },

            /**
             * Set sizes for renderer and aspect for camera.
             */
            onWindowResize() {
                let elem = document.getElementById('ocean');

                if (!elem) return;

                let width = document.getElementById('ocean').offsetWidth;

                let height = (width / 16) * 9;

                if (window.innerWidth < 600) {
                    height = 510;
                }

                // Set height for mobile devices
                if (
                    window.innerWidth < 600 &&
                    window.innerHeight < window.innerWidth
                ) {
                    height = window.innerHeight;
                }

                if (window.innerHeight < 480) {
                    height = window.innerHeight;
                }

                camera.aspect = width / window.innerHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(width, height);
            },

            /**
             * Add or remove Rain in scene based on mood prop.
             */
            updateRain() {
                if (this.mood <= 40) {
                    scene.add(rain);
                } else {
                    scene.remove(rain);
                }
            },

            /**
             * Update Sun params based on mood prop.
             */
            updateSun() {
                const azimuth = 0.347;
                const inclination = 0.42 + this.mood * 0.0005;
                const distance = 180;

                let theta = Math.PI * (inclination - 0.5);
                let phi = 2 * Math.PI * (azimuth - 0.5);
                light.position.x = distance * Math.cos(phi);
                light.position.y = distance * Math.sin(phi) * Math.sin(theta);
                light.position.z =
                    distance * Math.sin(phi / 0.7) * Math.cos(theta / 4);
                sky.material.uniforms['sunPosition'].value = light.position.copy(
                    light.position
                );
                water.material.uniforms['sunDirection'].value
                    .copy(light.position)
                    .normalize();

                cubeCamera.update(renderer, sky);
            },

            /**
             * Update Water params based on mood prop.
             */
            updateWater() {
                let normalization =
                    this.mood > 49 ? this.mood * 2 : this.mood * 0.1;

                if (this.mood > 85) {
                    normalization = 800;
                    water.material.uniforms['alpha'].value = 0.77;
                } else {
                    water.material.uniforms['alpha'].value = 1;
                }

                water.material.uniforms['time'].value += 1.0 / (30 + normalization);
            },

            /**
             * Animate Ocean and rain.
             */
            animate() {
                animationFrame = requestAnimationFrame(this.animate);
                this.render();

                rainGeo.vertices.forEach(p => {
                    p.velocity -= 0.1 + Math.random() * 0.1;
                    p.y += p.velocity;
                    if (p.y < -200) {
                        p.y = 200;
                        p.velocity = 0;
                    }
                });
                rainGeo.verticesNeedUpdate = true;
                rain.rotation.y += 0.001;
                this.render();
            },

            // Update Ocean and render Ocean.
            render() {
                this.updateWater();
                renderer.render(scene, camera);
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import "../../../assets/style/_variables.styl";

    .mood
        position: relative
</style>
